/**
 * SmartAssistPanel — Renders the chat sidebar inside Layout.
 *
 * Lazy-loads AssistantSidebar + BridgeSync from the plugin.
 * Exposes the sidebar's occupied width via SidebarLayoutContext so that
 * Layout can shrink main content to make room (side-by-side).
 */
import React, {
  Suspense,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { useSmartAssistBridge } from "../SmartAssistBridge";
import { useAuthState } from "../AuthProvider";

/* ── Context for sidebar dimensions ─────────────────────── */
const SidebarLayoutCtx = createContext({
  sidebarOffset: 0,
  setSidebarOffset: () => {},
});
export const useSidebarLayout = () => useContext(SidebarLayoutCtx);

export function SidebarLayoutProvider({ children }) {
  const [sidebarOffset, setSidebarOffset] = useState(0);
  return (
    <SidebarLayoutCtx.Provider value={{ sidebarOffset, setSidebarOffset }}>
      {children}
    </SidebarLayoutCtx.Provider>
  );
}

/* ── Tiny bridge that reads the plugin sidebar state and writes offset ── */
const LazySidebarReporter = React.lazy(() =>
  import("eimp-chat-plugin").then((mod) => {
    // Build a small component that reads useAssistant and reports to Layout
    const Reporter = () => {
      const { sidebarOpen, sidebarWidth } = mod.useAssistant();
      const { setSidebarOffset } = useSidebarLayout();
      useEffect(() => {
        setSidebarOffset(sidebarOpen ? sidebarWidth : 0);
      }, [sidebarOpen, sidebarWidth, setSidebarOffset]);
      return null;
    };
    return { default: Reporter };
  }),
);

const LazySidebar = React.lazy(() =>
  import("eimp-chat-plugin").then((mod) => ({
    default: mod.AssistantSidebar,
  })),
);

const LazyBridgeSync = React.lazy(() =>
  import("eimp-chat-plugin").then((mod) => ({
    default: mod.BridgeSync,
  })),
);

/* ── Reports sidebarOffset as 0 when sidebar is hidden on non-EID pages ── */
const LazySidebarOffsetClearer = React.lazy(() =>
  import("eimp-chat-plugin").then((mod) => {
    const Clearer = () => {
      const { setSidebarOffset } = useSidebarLayout();
      useEffect(() => {
        setSidebarOffset(0);
        return () => {};
      }, [setSidebarOffset]);
      return null;
    };
    return { default: Clearer };
  }),
);

export default function SmartAssistPanel() {
  const { pageContext, updatePageContext } = useSmartAssistBridge();
  const { isAuthenticated } = useAuthState();
  const location = useLocation();

  // Only show on EID view pages: /search-identities-new/:clientId/view/:eid/
  const isEidPage = /^\/search-identities-new\/[^/]+\/view\/[^/]+/.test(
    location.pathname,
  );

  if (!isAuthenticated) return null;

  // On non-EID pages, clear the sidebar offset but keep provider state intact
  if (!isEidPage) {
    return (
      <Suspense fallback={null}>
        <LazySidebarOffsetClearer />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <LazyBridgeSync
        goldenId={pageContext.goldenId}
        pageName={pageContext.pageName}
        updatePageContext={updatePageContext}
      />
      <LazySidebarReporter />
      <LazySidebar />
    </Suspense>
  );
}
